import { useTheme } from '@canopysecurity/component-library';
import { LoadingButton } from '@mui/lab';
import Papa from 'papaparse';
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { associateTag } from '../../services/TagsService';
import { FormInput, FormLabelContainer } from '../../StyledComponents/Form';
import { ParagraphText } from '../../StyledComponents/Typography';
import { ModalContent } from './ModalContent';
import { TagKnown } from '../../Pages/ManageTags/TagsTable';

interface AssociateDeviceTagPanelProps {
  tag: TagKnown;
  onClose: () => void;
}

export const AssociateDeviceTagPanel = (
  props: AssociateDeviceTagPanelProps,
) => {
  const theme = useTheme();
  const { tag, onClose } = props;
  const [file, setFile] = useState<File | null>(null);
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);

  const allSerialNumbers = useMemo(
    () =>
      serialNumbers.length ? serialNumbers : serialNumber ? [serialNumber] : [],
    [serialNumber, serialNumbers],
  );

  const handleSelectFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        setFile(event.target.files[0]);
        Papa.parse(event.target.files[0], {
          header: false,
          skipEmptyLines: true,
          complete(results) {
            const serialNumberArray = results.data
              .filter(Boolean)
              .flat() as string[];
            setSerialNumbers(serialNumberArray);
          },
        });
      }
    },
    [],
  );

  const {
    mutateAsync: associateMutate,
    isPending: loading,
    error: associateTagError,
  } = useMutation({
    mutationFn: (sn: string) =>
      associateTag({
        serialNumber: sn,
        tagName: tag.tagName,
      }),
  });

  const handleAssociateTag = async () => {
    if (tag.tagName) {
      const associations = allSerialNumbers
        .filter((sn) => sn.length)
        .map((sn) => associateMutate(sn));
      await Promise.all(associations);
    }
    onClose();
  };

  return (
    <ModalContent
      title={`Associate Device(s) to Tag: ${tag.tagName}`}
      loading={loading}
      error={associateTagError}
      handleClose={onClose}
      submitDisabled={loading || !allSerialNumbers.length}
      handleSubmit={handleAssociateTag}
      buttonLabels={{ loading: 'Associating...', create: 'Associate' }}
    >
      <FormLabelContainer>
        <FormInput
          placeholder="Enter a serial number"
          maxLength={120}
          defaultValue={serialNumber}
          autoCapitalize="none"
          onChange={(event): void => {
            setFile(null);
            setSerialNumbers([]);
            setSerialNumber(event.target.value);
          }}
        />
      </FormLabelContainer>
      <ParagraphText>OR</ParagraphText>
      <LoadingButton
        variant="outlined"
        component="label"
        style={{
          color: theme.primary.main,
          borderColor: theme.primary.main,
        }}
      >
        Upload CSV{file ? ` (${file.name})` : ''}
        <input type="file" hidden accept=".csv" onChange={handleSelectFile} />
      </LoadingButton>
    </ModalContent>
  );
};
